import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const API_URL = '';

const BusinessAuthContext = createContext(null);

export function BusinessAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setSubscription(data.subscription || null);
        setBusiness(data.business || null);
      } else {
        setUser(null);
        setSubscription(null);
        setBusiness(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const requestMagicLink = useCallback(async (email) => {
    const response = await fetch(`${API_URL}/api/partner/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        email,
        origin_url: window.location.origin
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to send magic link');
    }
    
    return response.json();
  }, []);

  const verifyMagicLink = useCallback(async (token) => {
    const response = await fetch(`${API_URL}/api/partner/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token })
    });
    
    if (!response.ok) {
      throw new Error('Invalid or expired link');
    }
    
    const data = await response.json();
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/partner/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setUser(null);
    setSubscription(null);
    setBusiness(null);
  }, []);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, []);

  const value = useMemo(() => ({
    user, loading, subscription, business,
    requestMagicLink, verifyMagicLink, logout, refreshUser
  }), [user, loading, subscription, business, requestMagicLink, verifyMagicLink, logout, refreshUser]);

  return (
    <BusinessAuthContext.Provider value={value}>
      {children}
    </BusinessAuthContext.Provider>
  );
}

export function useBusinessAuth() {
  const context = useContext(BusinessAuthContext);
  if (!context) {
    throw new Error('useBusinessAuth must be used within BusinessAuthProvider');
  }
  return context;
}
